import { useEffect, useMemo, useState } from 'react';
import { GridActionsCellItem, GridRowModes, useGridApiRef } from '@mui/x-data-grid-premium';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { dataGridPremiumColumns } from '../../utils/helper';
import {
  AutoCompleteSelectUI,
  ButtonUI,
  DatagridPremiumUi,
  DeleteDialogUI,
  FileUploadDialogUI,
  InputUI,
} from '../Interface';
import { DataGridIdentifiers } from '../Interface/Datagrid/DataGridIdentifiers';

const SupportingDocumentTypeReports = ({
  reports,
  setReports,
  languageOptions,
  canEdit,
  isSaving,
  disabled,
  onFilesAdded,
  onDeleteReport,
}) => {
  const apiRef = useGridApiRef();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  useEffect(() => {
    if (!disabled) return;

    const rowsInEditMode = Object.keys(apiRef.current?.state?.editRows ?? {});
    for (const rowId of rowsInEditMode) {
      apiRef.current.stopRowEditMode({ id: rowId, ignoreModifications: false });
    }
  }, [apiRef, disabled]);

  const languageLabels = useMemo(
    () => Object.fromEntries(languageOptions.map((language) => [language.value, language.label])),
    [languageOptions]
  );

  const updateRow = (rowId, changes) => {
    setReports((currentReports) =>
      currentReports.map((report) => (report.id === rowId ? { ...report, ...changes } : report))
    );
  };

  const startEditRow = (rowId) => {
    const rowsInEditMode = Object.keys(apiRef.current?.state?.editRows ?? {});

    for (const currentRowId of rowsInEditMode) {
      if (rowId.toString() !== currentRowId) {
        apiRef.current.stopRowEditMode({ id: currentRowId, ignoreModifications: false });
      }
    }

    const rowMode = apiRef.current.getRowMode(rowId);
    if (rowMode === GridRowModes.View) {
      apiRef.current.startRowEditMode({ id: rowId });
    }
  };

  const triggerDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDownload = async (row) => {
    if (row.file instanceof File) {
      const url = URL.createObjectURL(row.file);
      triggerDownload(url, row.name);
      URL.revokeObjectURL(url);
      return;
    }

    if (row.path) {
      const url = `${process.env.REACT_APP_API_URL}/${row.path}`;
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      triggerDownload(blobUrl, row.name);
      URL.revokeObjectURL(blobUrl);
    }
  };

  const handlePreview = (row) => {
    if (row.file instanceof File) {
      const url = URL.createObjectURL(row.file);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }

    if (row.path) {
      window.open(`${process.env.REACT_APP_API_URL}/${row.path}`, '_blank');
    }
  };

  const columns = dataGridPremiumColumns([
    {
      field: 'name',
      headerName: 'Megnevezés',
      type: 'string',
      minWidth: 180,
    },
    {
      field: 'label',
      headerName: 'Címke',
      type: 'string',
      minWidth: 160,
      editable: true,
      renderEditCell: (params) => (
        <div onClick={(event) => event.stopPropagation()} className="w-full">
          <InputUI
            variant="standard"
            value={params.row.label ?? ''}
            onChange={(event) => {
              const nextValue = event.target.value;
              apiRef.current.setEditCellValue({
                id: params.id,
                field: params.field,
                value: nextValue,
              });
              updateRow(params.id, { label: nextValue });
            }}
          />
        </div>
      ),
    },
    {
      field: 'langCode',
      headerName: 'Nyelv',
      type: 'string',
      minWidth: 160,
      editable: true,
      valueGetter: (_value, row) => languageLabels[row.langCode] ?? row.langCode ?? '',
      renderEditCell: (params) => (
        <div onClick={(event) => event.stopPropagation()} className="w-full">
          <AutoCompleteSelectUI
            id={`supporting-document-report-lang-${params.id}`}
            fullWidth
            dataset={languageOptions}
            selectedValue={params.row.langCode}
            selectedLabelValue={languageLabels[params.row.langCode] ?? ''}
            onChange={(_event, newValue) => {
              const nextValue = newValue?.value ?? null;
              apiRef.current.setEditCellValue({
                id: params.id,
                field: params.field,
                value: nextValue,
              });
              updateRow(params.id, { langCode: nextValue });
            }}
          />
        </div>
      ),
    },
    {
      field: 'extension',
      headerName: 'Kiterjesztés',
      type: 'string',
      minWidth: 130,
    },
    {
      field: 'path',
      headerName: 'Elérési út',
      type: 'string',
      minWidth: 240,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Műveletek',
      minWidth: 100,
      getActions: ({ row }) => {
        const previewable = ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'].includes(
          (row.extension || '').toLowerCase()
        );

        return [
          ...(previewable
            ? [
                <GridActionsCellItem
                  key="preview"
                  icon={<VisibilityIcon />}
                  label="Előnézet"
                  disabled={!row.file && !row.path}
                  onClick={() => handlePreview(row)}
                />,
              ]
            : []),
          <GridActionsCellItem
            key="download"
            icon={<DownloadIcon />}
            label="Letöltés"
            disabled={!row.file && !row.path}
            onClick={() => handleDownload(row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Törlés"
            disabled={!canEdit || disabled || isSaving}
            onClick={() => setReportToDelete(row)}
          />,
        ];
      },
    },
  ]);

  return (
    <div>
      <div className="flex justify-end mb-2">
        <ButtonUI
          type="button"
          text="Új fájl"
          className="bg-success"
          disabled={disabled || !canEdit || isSaving}
          onClick={() => setUploadDialogOpen(true)}
        />
      </div>
      <DatagridPremiumUi
        apiRef={apiRef}
        datagridIdentifier={DataGridIdentifiers.supportingDocumentTypeReports}
        dataLoader={() => {}}
        datagridInitialState={{
          sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
        }}
        rows={reports}
        columns={columns}
        editMode="row"
        height={360}
        disableSave
        headerFilters={false}
        disableRowSelectionOnClick
        onCellDoubleClick={(_params, event) => {
          event.defaultMuiPrevented = true;
        }}
        onCellClick={(params) => {
          if (disabled || !canEdit || isSaving || params.field === 'actions') return;
          startEditRow(params.row.id);
        }}
        onRowEditStop={() => {}}
      />
      <FileUploadDialogUI
        open={uploadDialogOpen}
        accept=".rptdesign"
        handleClose={() => setUploadDialogOpen(false)}
        handleUpload={(files) => {
          if (onFilesAdded(files) !== false) {
            setUploadDialogOpen(false);
          }
        }}
        disabled={disabled || isSaving}
      />
      <DeleteDialogUI
        show={Boolean(reportToDelete)}
        onHide={() => setReportToDelete(null)}
        handleRemoveElement={async () => {
          await onDeleteReport(reportToDelete);
          setReportToDelete(null);
        }}
        disabled={isSaving}
      />
    </div>
  );
};

export default SupportingDocumentTypeReports;
